import { useRef, useEffect, useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import { useProjectStore } from '@/store/projectStore';
import { formatTime } from '@/types';
import type { VideoMetadata } from '@/types';
import clsx from 'clsx';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  SkipBack,
  SkipForward,
  Scissors,
  Upload,
  Film,
  Sparkles,
  Loader2,
} from 'lucide-react';

// A short, publicly-accessible sample MP4 (~2.5 MB, 720p, 15 s) from Google's open media test bucket.
const SAMPLE_VIDEO_URL =
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4';

interface VideoPlayerProps {
  onTimeUpdate?: (time: number) => void;
  onVideoLoaded?: (video: HTMLVideoElement) => void;
  canvasOverlay?: React.ReactNode;
}

export interface VideoPlayerHandle {
  getVideoElement: () => HTMLVideoElement | null;
  getVideoContainerRect: () => DOMRect | null;
}

export const VideoPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>(({ onTimeUpdate, onVideoLoaded, canvasOverlay }, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  
  // Expose video element and container through ref
  useImperativeHandle(ref, () => ({
    getVideoElement: () => videoRef.current,
    getVideoContainerRect: () => videoContainerRef.current?.getBoundingClientRect() ?? null,
  }));
  const progressRef = useRef<HTMLDivElement>(null);
  const [isDraggingProgress, setIsDraggingProgress] = useState(false);
  const [dragTime, setDragTime] = useState<number | null>(null); // Local state for smooth dragging
  const lastStoreUpdateRef = useRef<number>(0);
  
  const [sampleLoading, setSampleLoading] = useState(false);

  const video = useProjectStore((state) => state.project.video);
  const trimRange = useProjectStore((state) => state.project.trimRange);
  const playerState = useProjectStore((state) => state.playerState);
  const setIsPlaying = useProjectStore((state) => state.setIsPlaying);
  const setCurrentTime = useProjectStore((state) => state.setCurrentTime);
  const setVolume = useProjectStore((state) => state.setVolume);
  const toggleMute = useProjectStore((state) => state.toggleMute);
  const setPlaybackRate = useProjectStore((state) => state.setPlaybackRate);
  const setVideo = useProjectStore((state) => state.setVideo);

  // Load the built-in sample video so new users can try without uploading a file
  const handleLoadSampleVideo = useCallback(async () => {
    setSampleLoading(true);
    try {
      const response = await fetch(SAMPLE_VIDEO_URL);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      // Read metadata via a temporary video element
      const tempVideo = document.createElement('video');
      tempVideo.src = url;
      await new Promise<void>((resolve, reject) => {
        tempVideo.onloadedmetadata = () => resolve();
        tempVideo.onerror = () => reject(new Error('Failed to read sample video'));
      });

      const metadata: VideoMetadata = {
        path: url,
        name: 'sample-video.mp4',
        duration: tempVideo.duration,
        width: tempVideo.videoWidth,
        height: tempVideo.videoHeight,
        fps: 30,
        codec: 'h264',
        fileSize: blob.size,
      };

      setVideo(metadata, blob as File);
    } catch (err) {
      console.error('[Overlay Studio] Failed to load sample video:', err);
    } finally {
      setSampleLoading(false);
    }
  }, [setVideo]);
  
  // Get effective trim bounds
  const trimStart = trimRange?.enabled ? trimRange.startTime : 0;
  const trimEnd = trimRange?.enabled ? trimRange.endTime : (video?.duration ?? 0);
  const isTrimEnabled = trimRange?.enabled ?? false;
  const trimmedDuration = trimEnd - trimStart;
  
  // Sync video element with state
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    
    if (playerState.isPlaying) {
      videoEl.play().catch(() => {
        setIsPlaying(false);
      });
    } else {
      videoEl.pause();
    }
  }, [playerState.isPlaying, setIsPlaying]);
  
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    videoEl.volume = playerState.isMuted ? 0 : playerState.volume;
  }, [playerState.volume, playerState.isMuted]);
  
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    videoEl.playbackRate = playerState.playbackRate;
  }, [playerState.playbackRate]);
  
  // Handle time updates from video element - respect trim bounds
  const handleTimeUpdate = useCallback(() => {
    const videoEl = videoRef.current;
    if (!videoEl || isDraggingProgress) return;
    
    const currentTime = videoEl.currentTime;
    
    // If trim is enabled and we've gone past the end, loop back to start
    if (isTrimEnabled && currentTime >= trimEnd) {
      videoEl.currentTime = trimStart;
      setCurrentTime(trimStart);
      onTimeUpdate?.(trimStart);
      return;
    }
    
    // If trim is enabled and we're before the start, jump to start
    if (isTrimEnabled && currentTime < trimStart && playerState.isPlaying) {
      videoEl.currentTime = trimStart;
      setCurrentTime(trimStart);
      onTimeUpdate?.(trimStart);
      return;
    }
    
    setCurrentTime(currentTime);
    onTimeUpdate?.(currentTime);
  }, [setCurrentTime, onTimeUpdate, isDraggingProgress, isTrimEnabled, trimStart, trimEnd, playerState.isPlaying]);
  
  // Handle video loaded
  const handleLoadedMetadata = useCallback(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    onVideoLoaded?.(videoEl);
  }, [onVideoLoaded]);
  
  // Handle play/pause - start from trim start if before it
  const handlePlayPause = useCallback(() => {
    const videoEl = videoRef.current;
    if (videoEl && isTrimEnabled && !playerState.isPlaying) {
      // If about to play and we're outside trim range, jump to trim start
      if (videoEl.currentTime < trimStart || videoEl.currentTime >= trimEnd) {
        videoEl.currentTime = trimStart;
        setCurrentTime(trimStart);
      }
    }
    setIsPlaying(!playerState.isPlaying);
  }, [setIsPlaying, playerState.isPlaying, isTrimEnabled, trimStart, trimEnd, setCurrentTime]);
  
  // Calculate time from mouse position
  const getTimeFromMouseEvent = useCallback((clientX: number): number => {
    const progressEl = progressRef.current;
    if (!progressEl || !video) return 0;
    
    const rect = progressEl.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return percent * video.duration;
  }, [video]);
  
  // Handle progress bar click (instant seek)
  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (isDraggingProgress) return; // Don't handle click if we were dragging
    
    const videoEl = videoRef.current;
    if (!videoEl || !video) return;
    
    const time = getTimeFromMouseEvent(e.clientX);
    videoEl.currentTime = time;
    setCurrentTime(time);
  }, [video, setCurrentTime, getTimeFromMouseEvent, isDraggingProgress]);
  
  // Handle progress drag - optimized for smoothness
  const handleProgressMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const videoEl = videoRef.current;
    if (!videoEl || !video) return;
    
    const wasPlaying = playerState.isPlaying;
    if (wasPlaying) {
      videoEl.pause();
    }
    
    setIsDraggingProgress(true);
    const initialTime = getTimeFromMouseEvent(e.clientX);
    setDragTime(initialTime);
    videoEl.currentTime = initialTime;
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const time = getTimeFromMouseEvent(moveEvent.clientX);
      
      // Update local state immediately for smooth visual feedback
      setDragTime(time);
      
      // Update video element directly (bypassing store for performance)
      if (videoRef.current) {
        videoRef.current.currentTime = time;
      }
      
      // Throttle store updates to every 50ms for timeline sync
      const now = Date.now();
      if (now - lastStoreUpdateRef.current > 50) {
        lastStoreUpdateRef.current = now;
        setCurrentTime(time);
      }
    };
    
    const handleMouseUp = (upEvent: MouseEvent) => {
      const finalTime = getTimeFromMouseEvent(upEvent.clientX);
      
      // Final update to store
      setCurrentTime(finalTime);
      setDragTime(null);
      setIsDraggingProgress(false);
      
      // Resume playback if it was playing before
      if (wasPlaying && videoRef.current) {
        videoRef.current.play().catch(() => {});
      }
      
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [video, playerState.isPlaying, getTimeFromMouseEvent, setCurrentTime]);
  
  // Skip forward/backward - respect trim bounds
  const handleSkip = useCallback((seconds: number) => {
    const videoEl = videoRef.current;
    if (!videoEl || !video) return;
    
    let newTime = videoEl.currentTime + seconds;
    
    // Clamp within trim bounds if trim is enabled
    if (isTrimEnabled) {
      newTime = Math.max(trimStart, Math.min(trimEnd, newTime));
    } else {
      newTime = Math.max(0, Math.min(video.duration, newTime));
    }
    
    videoEl.currentTime = newTime;
    setCurrentTime(newTime);
  }, [video, setCurrentTime, isTrimEnabled, trimStart, trimEnd]);
  
  // Handle video end
  const handleEnded = useCallback(() => {
    if (isTrimEnabled) {
      // Loop within trim range
      const videoEl = videoRef.current;
      if (videoEl) {
        videoEl.currentTime = trimStart;
        setCurrentTime(trimStart);
      }
    } else {
      setIsPlaying(false);
    }
  }, [setIsPlaying, isTrimEnabled, trimStart, setCurrentTime]);
  
  // Calculate progress percentage - use dragTime during drag for smooth visual
  const displayTime = isDraggingProgress && dragTime !== null ? dragTime : playerState.currentTime;
  const progressPercent = video ? (displayTime / video.duration) * 100 : 0;
  const trimStartPercent = video ? (trimStart / video.duration) * 100 : 0;
  const trimEndPercent = video ? (trimEnd / video.duration) * 100 : 100;
  
  if (!video) {
    return (
      <div className="relative w-full h-full flex items-center justify-center video-container rounded-lg p-8">
        <div className="drop-zone w-full max-w-lg p-12 text-center">
          {/* Animated icon */}
          <div className="relative mx-auto w-24 h-24 mb-6">
            {/* Pulse rings */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 opacity-20 animate-pulse-ring" />
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 opacity-20 animate-pulse-ring" style={{ animationDelay: '0.5s' }} />
            
            {/* Main icon container */}
            <div className="relative w-full h-full rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center animate-float">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                <Film size={28} className="text-white" />
              </div>
            </div>
          </div>
          
          {/* Text content */}
          <h3 className="text-xl font-semibold mb-2 bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-gradient">
            Drop Your Video Here
          </h3>
          <p className="text-sm text-editor-text-muted mb-6">
            or click <span className="text-indigo-400 font-medium">Import Video</span> in the toolbar
          </p>
          
          {/* Supported formats */}
          <div className="flex items-center justify-center gap-2 text-xs text-editor-text-dim">
            <span className="px-2 py-1 rounded bg-editor-surface">MP4</span>
            <span className="px-2 py-1 rounded bg-editor-surface">WebM</span>
            <span className="px-2 py-1 rounded bg-editor-surface">MOV</span>
            <span className="px-2 py-1 rounded bg-editor-surface">AVI</span>
          </div>
          
          {/* Upload hint */}
          <div className="mt-6 flex items-center justify-center gap-2 text-editor-text-muted">
            <Upload size={14} className="animate-bounce" style={{ animationDuration: '2s' }} />
            <span className="text-xs">Drag & drop supported</span>
          </div>

          {/* Sample video button */}
          <div className="mt-5 border-t border-white/5 pt-5">
            <p className="text-xs text-editor-text-dim mb-3">No video handy?</p>
            <button
              onClick={handleLoadSampleVideo}
              disabled={sampleLoading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-500/15 hover:bg-indigo-500/25 border border-indigo-500/30 text-indigo-300 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sampleLoading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Loading sample…
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  Try with a sample video
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="relative w-full h-full flex flex-col">
      {/* Video container - only this area should have overlays */}
      <div 
        ref={videoContainerRef}
        className="flex-1 relative video-container rounded-t-lg overflow-hidden"
      >
        <video
          ref={videoRef}
          src={video.path}
          className="absolute inset-0 w-full h-full object-contain"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
          playsInline
        />
        {/* Canvas overlay for drawing - positioned inside video container only */}
        {canvasOverlay}
      </div>
      
      {/* Controls */}
      <div className="glass-panel border-t border-white/5 p-3 rounded-b-lg">
        {/* Progress bar with trim visualization */}
        <div
          ref={progressRef}
          className={clsx(
            "h-2 bg-editor-surface rounded-full cursor-pointer mb-3 group relative",
            isDraggingProgress && "cursor-grabbing"
          )}
          onMouseDown={handleProgressMouseDown}
          onClick={handleProgressClick}
        >
          {/* Trim region indicator */}
          {isTrimEnabled && (
            <>
              {/* Dimmed region before trim start */}
              {trimStartPercent > 0 && (
                <div 
                  className="absolute inset-y-0 bg-black/40 rounded-l-full pointer-events-none"
                  style={{ left: 0, width: `${trimStartPercent}%` }}
                />
              )}
              {/* Dimmed region after trim end */}
              {trimEndPercent < 100 && (
                <div 
                  className="absolute inset-y-0 bg-black/40 rounded-r-full pointer-events-none"
                  style={{ left: `${trimEndPercent}%`, right: 0 }}
                />
              )}
              {/* Trim boundary markers */}
              <div 
                className="absolute inset-y-0 w-0.5 bg-yellow-400 z-10 pointer-events-none"
                style={{ left: `${trimStartPercent}%` }}
              />
              <div 
                className="absolute inset-y-0 w-0.5 bg-yellow-400 z-10 pointer-events-none"
                style={{ left: `${trimEndPercent}%` }}
              />
            </>
          )}
          
          {/* Progress fill - no transition during drag for instant feedback */}
          <div
            className={clsx(
              "h-full bg-gradient-to-r from-editor-accent to-purple-500 rounded-full relative",
              !isDraggingProgress && "transition-[width] duration-100"
            )}
            style={{ width: `${progressPercent}%` }}
          >
            <div className={clsx(
              "absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg transition-opacity",
              isDraggingProgress ? "opacity-100 scale-125" : "opacity-0 group-hover:opacity-100"
            )} />
          </div>
        </div>
        
        {/* Control buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Skip back */}
            <button
              onClick={() => handleSkip(-5)}
              className="btn btn-ghost btn-icon"
              title="Skip back 5s"
            >
              <SkipBack size={18} />
            </button>
            
            {/* Play/Pause */}
            <button
              onClick={handlePlayPause}
              className="btn btn-primary btn-icon w-10 h-10"
              title={playerState.isPlaying ? 'Pause (Space)' : 'Play (Space)'}
            >
              {playerState.isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
            </button>
            
            {/* Skip forward */}
            <button
              onClick={() => handleSkip(5)}
              className="btn btn-ghost btn-icon"
              title="Skip forward 5s"
            >
              <SkipForward size={18} />
            </button>
            
            {/* Volume */}
            <div className="flex items-center gap-2 ml-2">
              <button
                onClick={toggleMute}
                className="btn btn-ghost btn-icon"
                title={playerState.isMuted ? 'Unmute' : 'Mute'}
              >
                {playerState.isMuted || playerState.volume === 0 ? (
                  <VolumeX size={18} />
                ) : (
                  <Volume2 size={18} />
                )}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={playerState.isMuted ? 0 : playerState.volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="w-20"
              />
            </div>
          </div>
          
          {/* Time display */}
          <div className="flex items-center gap-4 text-sm font-mono">
            {/* Trim indicator */}
            {isTrimEnabled && (
              <div className="flex items-center gap-1 text-yellow-400 text-xs bg-yellow-400/10 px-2 py-0.5 rounded">
                <Scissors size={12} />
                <span>{formatTime(trimmedDuration)}</span>
              </div>
            )}
            
            <span className="text-editor-text">
              {formatTime(displayTime)}
            </span>
            <span className="text-editor-text-dim">/</span>
            <span className={clsx(
              isTrimEnabled ? 'text-yellow-400' : 'text-editor-text-muted'
            )}>
              {formatTime(isTrimEnabled ? trimmedDuration : video.duration)}
            </span>
          </div>
          
          {/* Playback rate */}
          <div className="flex items-center gap-2">
            <select
              value={playerState.playbackRate}
              onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
              className="bg-editor-surface border border-editor-border rounded px-2 py-1 text-sm"
            >
              <option value="0.25">0.25x</option>
              <option value="0.5">0.5x</option>
              <option value="0.75">0.75x</option>
              <option value="1">1x</option>
              <option value="1.25">1.25x</option>
              <option value="1.5">1.5x</option>
              <option value="2">2x</option>
            </select>
            
            <button className="btn btn-ghost btn-icon" title="Fullscreen">
              <Maximize size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

VideoPlayer.displayName = 'VideoPlayer';
